import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Search, UserPlus } from "lucide-react";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AuthLayout, Field, fieldCls } from "../components/auth/AuthLayout";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { DEFAULT_CONTENT } from "../lib/content";

const COUNTRIES = DEFAULT_CONTENT.paises.list;

const SponsorPicker = ({ sponsor, setSponsor, initial }) => {
  const [q, setQ] = useState(initial);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (sponsor || !q.trim()) return undefined;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/public/sponsor", { params: { q } });
        setMatches(data);
        if (data.length === 1 && (q.replace(/\D/g, "").padStart(5, "0") === data[0].dist_id || q.trim().toLowerCase() === data[0].name.toLowerCase())) setSponsor(data[0]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q, sponsor, setSponsor]);

  if (sponsor)
    return (
      <div className="flex items-center justify-between rounded-xl border border-leaf/40 bg-leaf/10 px-4 py-3" data-testid="register-sponsor-selected">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-leaf" />
          <div>
            <p className="text-sm font-semibold">{sponsor.name}</p>
            <p className="font-mono text-xs text-leaf">ID {sponsor.dist_id}</p>
          </div>
        </div>
        <button type="button" onClick={() => { setSponsor(null); setQ(""); setMatches([]); }} className="text-xs text-white/70 underline-offset-2 hover:underline" data-testid="register-sponsor-change">Cambiar</button>
      </div>
    );
  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ID (ej. 00001) o nombre" className={`${fieldCls} pl-10`} data-testid="register-sponsor-input" />
        {searching && <Loader2 className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-white/60" />}
      </div>
      {q.trim() && !searching && !matches.length && <p className="mt-2 text-xs text-[#ffb3b5]" data-testid="register-sponsor-none">No encontramos a nadie con ese dato. Verifica el ID o nombre.</p>}
      {matches.length > 0 && (
        <ul className="mt-2 overflow-hidden rounded-xl border border-white/15 bg-ink-2" data-testid="register-sponsor-matches">
          {matches.map((m) => (
            <li key={m.dist_id}>
              <button type="button" onClick={() => setSponsor(m)} className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-white/5" data-testid={`register-sponsor-option-${m.dist_id}`}>
                <span>{m.name}</span><span className="font-mono text-xs text-leaf">ID {m.dist_id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function Register() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", phone: "", country: "México", password: "", confirm: "", accepted_terms: false, registration_type: "membresia_dorada" });
  const [sponsor, setSponsor] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (user) nav(user.role === "admin" ? "/admin" : "/oficina", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!sponsor) return setError("Indica la persona que te invita (ID o nombre).");
    if (form.password !== form.confirm) return setError("Las contraseñas no coinciden.");
    setLoading(true);
    try {
      await register({ name: form.name, phone: form.phone, country: form.country, password: form.password, sponsor_id: sponsor.dist_id, accepted_terms: form.accepted_terms, registration_type: form.registration_type });
      nav(form.registration_type === "membresia_dorada" ? "/oficina?tab=membresia" : "/oficina", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout testId="register-page" image="/img/filosofia-equipo.jpg">
      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-leaf text-ink"><UserPlus className="h-6 w-6" /></span>
      <h1 className="mt-8 font-display text-4xl font-semibold tracking-tight sm:text-5xl">Crear mi cuenta</h1>
      <p className="mt-3 text-base text-[#AEB7D3]">Regístrate como distribuidor. Recibirás tu número de ID único, tu enlace y tu código QR de invitación.</p>
      <form onSubmit={submit} className="mt-10 space-y-5" data-testid="register-form">
        <Field id="name" label="Nombre completo">
          <Input id="name" required minLength={3} value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldCls} data-testid="register-name" />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field id="phone" label="Celular">
            <Input id="phone" type="tel" inputMode="numeric" required placeholder="10 dígitos" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={fieldCls} data-testid="register-phone" />
          </Field>
          <Field id="country" label="País">
            <Select value={form.country} onValueChange={(v) => set("country", v)}>
              <SelectTrigger id="country" className={fieldCls} data-testid="register-country"><SelectValue /></SelectTrigger>
              <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field id="password" label="Contraseña">
            <Input id="password" type="password" required minLength={6} autoComplete="new-password" value={form.password} onChange={(e) => set("password", e.target.value)} className={fieldCls} data-testid="register-password" />
          </Field>
          <Field id="confirm" label="Confirmar contraseña">
            <Input id="confirm" type="password" required minLength={6} autoComplete="new-password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} className={fieldCls} data-testid="register-confirm" />
          </Field>
        </div>
        <Field id="sponsor" label="Persona que te invita (ID o nombre)">
          <SponsorPicker sponsor={sponsor} setSponsor={setSponsor} initial={params.get("ref") || ""} />
        </Field>
        <div>
          <p className="mb-2 block text-sm font-semibold text-white/85">Tipo de registro</p>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="register-type">
            {[
              ["membresia_dorada", "Registro con Membresía Dorada", "$299 USD anual · comisiones a 5 niveles por membresías + plataformas 100% para ti. Al terminar te llevamos a la pasarela de pago."],
              ["sin_membresia", "Registro sin Membresía", "Entras a la red y participas en las Ganancias Mensuales. Puedes activar tu membresía después."],
            ].map(([v, title, desc]) => (
              <button key={v} type="button" onClick={() => set("registration_type", v)} className={`rounded-2xl border p-4 text-left transition-colors ${form.registration_type === v ? "border-leaf bg-leaf/10" : "border-white/15 bg-white/5 hover:border-white/40"}`} data-testid={`register-type-${v}`}>
                <span className="flex items-center gap-2">
                  <span className={`grid h-5 w-5 place-items-center rounded-full border ${form.registration_type === v ? "border-leaf bg-leaf" : "border-white/40"}`}>{form.registration_type === v && <CheckCircle2 className="h-4 w-4 text-ink" />}</span>
                  <span className="text-sm font-semibold">{title}</span>
                </span>
                <span className="mt-2 block text-xs leading-relaxed text-[#AEB7D3]">{desc}</span>
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-start gap-3 text-sm text-white/80">
          <Checkbox checked={form.accepted_terms} onCheckedChange={(v) => set("accepted_terms", v === true)} className="mt-0.5 h-5 w-5 border-white/40 data-[state=checked]:bg-leaf data-[state=checked]:text-ink" data-testid="register-terms" />
          <span>
            Acepto los <Link to="/legal/terminos" target="_blank" className="font-semibold text-leaf underline underline-offset-2">Términos del servicio</Link> y el{" "}
            <Link to="/legal/privacidad" target="_blank" className="font-semibold text-leaf underline underline-offset-2">Aviso de Privacidad</Link>. Entiendo que participar no garantiza ingresos.
          </span>
        </label>
        {error && <p className="rounded-xl border border-coral/40 bg-coral/10 px-4 py-3 text-sm text-[#ffb3b5]" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={loading || !form.accepted_terms} className="btn-leaf w-full disabled:opacity-60" data-testid="register-btn">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Crear cuenta"}
        </button>
      </form>
      <p className="mt-8 text-sm text-[#AEB7D3]">
        ¿Ya tienes cuenta? <Link to="/login" className="font-semibold text-leaf underline-offset-4 hover:underline" data-testid="register-to-login">Ingresar</Link>
      </p>
    </AuthLayout>
  );
}
